import Head from 'next/head';
import { useRouter } from 'next/router';
import ErrorPage from 'next/error';
import Custom404 from '../../../404';
import { gql } from '@apollo/client';
import { useQuery } from '@helpers/async';
import { initializeApollo, addApolloState } from '@apollo-client';
import { Container, Box, Text, Flex, Grid, GridItem, Heading, AspectRatio } from '@chakra-ui/react';
import NextNprogress from 'nextjs-progressbar';

import GutenbergStylesheet from '../../../../styles/gutenberg';
import Layout from '@components/resources/Layout';
import RelatedPosts from '@components/resources/RelatedPosts';
import BlueCTA from '@components/resources/BlueCTA';
import ImageShimmer from '@components/ImageShimmer/ImageShimmer';

import sharecardImage from '@public/images/resources/sharecard-engineers.jpg';
import sitePath from '@helpers/sitePath';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import {
  primaryCategory,
  filterPostsByCategory,
  filterSinglePostByCategory,
} from '@helpers/categoryFilters';
import { addParamsToRelatedPost } from '@helpers/addParamsToRelatedPost';
import metaRobots from '@helpers/metaRobots';
import { ArrowLeftIcon, Button, Link } from '@terminal/design-system';

const GET_GLOBAL_ENGINEER = gql`
  query GetGlobalEngineer($slug: ID!) {
    globalEngineer(id: $slug, idType: SLUG) {
      id
      date
      title
      seo {
        title
        metaDesc
        metaRobotsNofollow
        metaRobotsNoindex
      }
      globalEngineerACF {
        date
        time
        text
        youtube
        speakers {
          name
          jobTitle
          image {
            sourceUrl
            altText
          }
        }
      }
      featuredImage {
        node {
          altText
          sourceUrl
        }
      }
      categories {
        edges {
          isPrimary
          node {
            name
          }
        }
      }
      relatedGlobalEngineer {
        posts {
          ... on GlobalEngineer {
            id
            slug
            title
            categories {
              edges {
                isPrimary
                node {
                  name
                }
              }
            }
            featuredImage {
              node {
                altText
                sourceUrl
              }
            }
          }
        }
      }
    }
    globalEngineers(first: 1000) {
      nodes {
        id
        date
        slug
        title
        categories {
          edges {
            isPrimary
            node {
              name
            }
          }
        }
        featuredImage {
          node {
            altText
            sourceUrl
          }
        }
      }
    }
  }
`;

export async function getServerSideProps({ query, locale }) {
  const { slug } = query;

  const apolloClient = initializeApollo();

  await apolloClient.query({
    query: GET_GLOBAL_ENGINEER,
    variables: {
      slug,
    },
  });

  return addApolloState(apolloClient, {
    props: {
      ...(await serverSideTranslations(locale)),
    },
  });
}

export default function GlobalEngineer() {
  const router = useRouter();

  function getUrlParams() {
    const routerQuery = router.asPath.split('?')[1];
    const urlParams = routerQuery && `?${routerQuery}`;
    if (routerQuery) {
      return urlParams;
    }
    return '';
  }

  const { slug } = router.query;

  const { loading, error, data } = useQuery(GET_GLOBAL_ENGINEER, {
    variables: {
      slug,
    },
  });

  // CSR loading
  if (loading)
    return (
      <NextNprogress
        color="#2D445D"
        startPosition={0.3}
        stopDelayMs={200}
        height={3}
        showOnShallow={true}
      />
    );
  // Apollo error
  if (error) return <ErrorPage statusCode="500" />;

  const post = data.globalEngineer;
  const relatedPosts = data.globalEngineer ? data.globalEngineer.relatedGlobalEngineer.posts : null;

  // get all post types
  const allResources = [].concat(data.globalEngineers.nodes);

  // sort by date
  const allSortedResources = allResources.sort((a, b) => (a.date < b.date ? 1 : -1));

  const latestPosts = filterPostsByCategory(
    allSortedResources,
    primaryCategory(data.globalEngineer),
  ).slice(0, 4);

  // add path for post type
  let relatedPostsWithParams = null;

  if (relatedPosts) {
    relatedPostsWithParams = addParamsToRelatedPost(relatedPosts);
  } else {
    relatedPostsWithParams = addParamsToRelatedPost(latestPosts);
  }

  if (post) {
    const postImage = post.featuredImage ? post.featuredImage.node.sourceUrl : sharecardImage.src;

    const date = new Date(post.globalEngineerACF.date).toLocaleString('en-US', {
      // year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    return (
      <>
        <Head>
          <link rel="canonical" href={sitePath(router.asPath)} />
          <title>{post.seo.title}</title>
          <meta name="description" content={post.seo.metaDesc} />
          <meta property="og:title" content={post.seo.title} />
          <meta property="og:description" content={post.seo.metaDesc} />
          <meta property="og:url" content={sitePath(router.asPath)} />
          <meta property="og:image" content={postImage} />
          <meta
            name="robots"
            content={metaRobots({
              translated: false,
              locale: router.locale,
              post,
            })}
          />
        </Head>
        <GutenbergStylesheet />
        <Layout type="engineers">
          <Container fontFamily="body">
            <Box py={3}>
              <Button
                as={Link}
                variant="ghost"
                size="sm"
                leftIcon={<ArrowLeftIcon />}
                href={
                  filterSinglePostByCategory(post, 'Demand')
                    ? '/resources'
                    : '/engineers/the-remote-engineer'
                }
              >
                Go back to{' '}
                {filterSinglePostByCategory(post, 'Demand') ? 'Resources' : 'The Remote Engineer'}
              </Button>
            </Box>
            <Grid
              templateColumns={{ base: '1fr', lg: '8fr 4fr' }}
              gap={{ base: '1.6rem', lg: '2rem 4.375rem' }}
              mb={{ base: '2.8rem', lg: '4.5rem' }}
            >
              <GridItem>
                <Box
                  fontFamily="body"
                  fontSize="0.625rem"
                  lineHeight="0.8125rem"
                  fontWeight="600"
                  color="#909090"
                  textTransform="uppercase"
                  letterSpacing="0.0625rem"
                >
                  The Global Engineer | {primaryCategory(post)}
                </Box>
                <Heading
                  as="h1"
                  fontSize={{
                    base: '1.8rem',
                    md: '2.25rem',
                  }}
                  fontWeight="600"
                  lineHeight={{
                    base: '2rem',
                    md: '2.5rem',
                  }}
                  mt="1rem"
                  color="#2D445D"
                >
                  {post.title}
                </Heading>
              </GridItem>
              <GridItem display={{ base: 'none', lg: 'block' }} />
              <GridItem order={{ lg: '1' }}>
                <Box
                  fontSize="0.75rem"
                  lineHeight="1.125rem"
                  fontWeight="700"
                  letterSpacing="0.0625rem"
                  textTransform="uppercase"
                  color="#909090"
                  mb="0.5rem"
                >
                  Event Date
                </Box>
                <Box
                  fontSize={{ base: '0.9rem', lg: '1.125rem' }}
                  lineHeight="1.5rem"
                  color="#2D445D"
                >
                  {date} • {post.globalEngineerACF.time} ET
                </Box>
                <Box
                  fontSize="0.75rem"
                  lineHeight="1.125rem"
                  fontWeight="700"
                  letterSpacing="0.0625rem"
                  textTransform="uppercase"
                  color="#909090"
                  mt={{ base: '1rem', lg: '2.5rem' }}
                  mb="1rem"
                >
                  Speakers
                </Box>
                {post.globalEngineerACF.speakers.map((speaker) => {
                  return (
                    <Flex key={speaker.name} alignItems="center" mb="1rem">
                      <Box
                        w={{ base: '4rem', lg: '5rem' }}
                        h={{ base: '4rem', lg: '5rem' }}
                        position="relative"
                        borderRadius="50%"
                        overflow="hidden"
                        mr="1.5rem"
                        flexShrink="0"
                      >
                        <ImageShimmer
                          src={speaker.image.sourceUrl}
                          alt={speaker.name}
                          style={{ objectFit: 'cover' }}
                          fill
                        />
                      </Box>
                      <Box color="#2D445D">
                        <Box
                          fontSize={{ base: '1rem', lg: '1.125rem' }}
                          lineHeight={{ base: '1.2rem', lg: '1.375rem' }}
                          fontWeight="700"
                          mb="0.125rem"
                        >
                          {speaker.name}
                        </Box>
                        <Box
                          fontSize={{ base: '1rem', lg: '1rem' }}
                          lineHeight={{ base: '1.2rem', lg: '1.375rem' }}
                          fontWeight="600"
                        >
                          {speaker.jobTitle}
                        </Box>
                      </Box>
                    </Flex>
                  );
                })}
              </GridItem>
              <GridItem order={{ lg: '0' }}>
                {post.globalEngineerACF.youtube && (
                  <AspectRatio maxW="100%" ratio={16 / 9}>
                    <iframe
                      src={`https://www.youtube.com/embed/${post.globalEngineerACF.youtube}`}
                      frameBorder="0"
                      title="video"
                    />
                  </AspectRatio>
                )}
                <Text
                  fontSize="0.9375rem"
                  lineHeight="1.5rem"
                  mt={{ base: '1.6rem', lg: '2rem' }}
                  dangerouslySetInnerHTML={{
                    __html: post.globalEngineerACF.text,
                  }}
                />
              </GridItem>
            </Grid>
          </Container>

          <RelatedPosts
            title="Recommended viewing"
            filteredOutId={post.id}
            posts={relatedPostsWithParams}
          />

          <BlueCTA type="engineers" />
        </Layout>
      </>
    );
  } else {
    return <Custom404 />;
  }
}
